Para evitar que el nombre de la familia se divida en dos líneas en pantallas pequeñas, se han agregado tres propiedades clave a `.nombre-protagonista`:

1. `white-space: nowrap;` para forzar a que permanezca en una sola línea.
2. `font-size: clamp(34px, 11vw, 52px);` que reduce dinámicamente el tamaño de la letra solo si la pantalla es muy pequeña.
3. `letter-spacing: -0.02em;` para compactar sutilmente los caracteres cuando el espacio falte.

Aquí tienes el código completo e intacto con esta solución integrada en los estilos:

```vue
<template>
  <div class="portada-xv" ref="rootRef" aria-label="Portada de XV">
    <!-- BOLA DISCO GIRANDO -->
    <img src="@/assets/diisco.png" class="bola bola-izquierda" alt="" />

    <!-- IMAGEN CON RECORTE + MOÑO -->
    <div class="imagen-xv-shell">
      <div class="imagen-xv-wrapper">
        <img
          src="@/assets/4.webp"
          class="imagen-xv"
          alt="Foto protagonista"
        />
      </div>

      <!-- 🎀 MOÑO FUERA DEL CONTENEDOR CON OVERFLOW -->
      <div class="mono-portada">
        <img
          :src="require('@/assets/moñi.png')"
          class="mono-portada-img"
          alt="Moño decorativo"
        />
      </div>
    </div>

    <!-- TEXTO PRINCIPAL -->
    <section class="texto-xv">
      <p class="frase-bendicion">
        Con la bendición de Dios <br />
        y de mis padres
      </p>

      <div class="separador-elegante" aria-hidden="true"></div>

      <h1 class="nombre-protagonista">Fam.&nbsp; Carpintero Casas</h1>

      <!-- 📝 Párrafo corregido con concordancia para la Familia -->
      <p class="parrafo-presentacion">
        Hoy compartimos la alegría de un sueño que se convierte en realidad. 
       <br /><br /> Los quince años de nuestra hija son el inicio de un viaje lleno de ilusiones, 
        llevando en el corazón el apoyo y cariño de todos ustedes. <br /><br />
        Los invitamos a compartir este momento mágico a nuestro lado, donde 
        juntos crearemos recuerdos inolvidables.
      </p>
    </section>
  </div>
</template>

<script>
import { onMounted, ref, nextTick } from 'vue'
import gsap from 'gsap'

export default {
  name: 'PortadaArley',
  setup() {
    const rootRef = ref(null)

    onMounted(async () => {
      await nextTick()

      const $ = (sel) => Array.from(gsap.utils.toArray(sel)).filter(Boolean)

      const foto = $('.imagen-xv-wrapper')
      const flores = ['.flor-1', '.flor-2', '.flor-3'].flatMap($)
      const texto = [
        '.frase-bendicion',
        '.separador-elegante',
        '.nombre-protagonista',
        '.parrafo-presentacion'
      ].flatMap($)

      gsap.set([...foto, ...flores, ...texto], { autoAlpha: 0, y: 28 })
      gsap.set(flores[0], { rotate: -8, scale: 0.95 })
      gsap.set(flores[1], { rotate: -18, scale: 0.94 })
      gsap.set(flores[2], { rotate: -12, scale: 0.94 })

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return

            const tl = gsap.timeline({ defaults: { ease: 'power2.out' } })

            tl.fromTo(
              foto,
              { scale: 0.94, y: 40, filter: 'blur(6px)' },
              { autoAlpha: 1, scale: 1, y: 0, filter: 'blur(0px)', duration: 1.15 }
            )

            tl.to(
              flores,
              {
                autoAlpha: 1,
                y: 0,
                rotate: (i) => [-2, -6, -4][i] || -4,
                duration: 0.9,
                stagger: 0.12
              },
              '-=0.55'
            )

            tl.to(
              texto,
              { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.15 },
              '-=0.25'
            )

            if (flores[0]) {
              gsap.to(flores[0], {
                y: '-=10',
                duration: 2.2,
                yoyo: true,
                repeat: -1,
                ease: 'sine.inOut'
              })
            }
            if (flores[1]) {
              gsap.to(flores[1], {
                y: '+=12',
                x: '-=4',
                duration: 2.6,
                yoyo: true,
                repeat: -1,
                ease: 'sine.inOut',
                delay: 0.2
              })
            }
            if (flores[2]) {
              gsap.to(flores[2], {
                y: '-=8',
                duration: 2.0,
                yoyo: true,
                repeat: -1,
                ease: 'sine.inOut',
                delay: 0.1
              })
            }

            gsap.to(foto, {
              scale: 1.01,
              duration: 3.2,
              yoyo: true,
              repeat: -1,
              ease: 'sine.inOut'
            })

            io.disconnect()
          })
        },
        { threshold: 0.45 }
      )

      if (rootRef.value) io.observe(rootRef.value)
    })

    return { rootRef }
  }
}
</script>

<style scoped>
.portada-xv{
  position:relative;
  width:100%;
  min-height:100vh;
  background:#fff;
  overflow:visible;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:flex-start;
  padding:48px 16px 56px;
}

/* Marco con recorte superior redondeado (look luxury) */
.imagen-xv-wrapper{
  width: min(92vw, 360px);
  height: calc(min(92vw, 360px) * 1.5);
  margin-top: 6px;
  border-radius: 180px 180px 0 0;
  overflow: hidden;
  z-index: 2;
  background:#fff;
  box-shadow:
    0 12px 40px rgba(0,0,0,.18),
    0 2px 8px rgba(0,0,0,.06);
  will-change: transform, filter, opacity;
  position: relative;
}
.imagen-xv{
  width:100%;
  height:100%;
  object-fit:cover;
  display:block;
}

/* 🎀 Moño en la parte superior derecha de la foto */
.mono-portada{
  position:absolute;
  top: -25px;
  right: -26px;
  z-index: 9995;
  transform: rotate(22deg);
}
.mono-portada-img{
  width: 240px;
  height: auto;
  display:block;
}

/* Texto */
.texto-xv{
  max-width: 620px;
  text-align:center;
  margin-top: 22px;
  padding: 0 8px;
  width: 100%;
}
.frase-bendicion{
  font-family: "Playfair Display", serif;
  font-size: clamp(18px, 2.6vw, 24px);
  color:#2f2e2e;
  line-height:1.5;
  margin: 20px 0 10px;
}
.separador-elegante{
  width:110px;
  height:2px;
  margin:12px auto 16px;
  background: linear-gradient(90deg, transparent, #d9b7c6, transparent);
  border-radius: 2px;
}
.nombre-protagonista{
  font-family: "titulos", cursive;
  font-size: clamp(34px, 11vw, 52px); /* Se reduce proporcionalmente en celulares muy pequeños */
  color:#161887;
  margin: 0 0 8px 0;
  line-height:1.1;
  white-space: nowrap; /* Evita que salte a una segunda línea */
  letter-spacing: -0.02em; /* Junta ligeramente las letras en pantallas reducidas */
}
.parrafo-presentacion{
  font-family: "quote", sans-serif;
  font-size: clamp(17px, 2.6vw, 18px);
  color:#3d3c3c;
  line-height:1.7;
  margin-top: 6px;
}

/* FLORES */
.flor{
  position:absolute;
  width:min(44vw, 240px);
  z-index:10;
  transform-origin:center;
  will-change: transform, opacity;
}
.flor-1{ top:-10px; left:-70px; z-index:5; }
.flor-2{ top:85px; right:-80px; transform: rotate(-80deg); z-index:1; width:min(60vw, 340px); }
.flor-3{ bottom:-50px; left:-90px; transform: rotate(-30deg); z-index:9;}

/* BOLA DISCO */
.bola{
  position:absolute;
  width:130px;
  animation: girar 10s linear infinite;
  opacity:.85;
  z-index:1;
  will-change: transform;
}
.bola-izquierda{ left:270px; bottom:-170px; z-index:9;}
@keyframes girar {
  0% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.9;
  }
}

/* Responsive refinado */
@media (min-width: 992px){
  .portada-xv{ padding-top:64px; }
  .imagen-xv-wrapper{ margin-top: 0; }
}
</style>

```